import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { PageHeader } from '../../../shared/components/layout/PageHeader';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { SidePanel, ResizableHandle } from '../../../shared/components/panel';
import { ledgerContainerSx } from '../../../shared/components/ledger';
import { useSitePanelState } from '../../sites/hooks/useSitePanelState';
import { useActivitySites } from '../hooks/useActivitySites';
import { useActivityList } from '../hooks/useActivityList';
import { useActivityUIStore } from '../stores/activityUIStore';
import { DateRangeControl } from '../components/DateRangeControl';
import { ActivityFilterContent } from '../components/ActivityFilterContent';
import { ExportActivityModal } from '../components/ExportActivityModal';
import { AllocationDetailsPanel } from '../../sites/components/panel/organisms/AllocationDetailsPanel';
import { ActivityLedger } from '../ledger/ActivityLedger';
import { ACTIVITY_COLLAPSE } from '../ledger/columns';
import {
  toActivityRows,
  type ActivityLedgerRowModel,
} from '../ledger/toActivityRows';

const MS_DAY = 86_400_000;

const STATUS_LABELS: Record<string, string> = {
  Booked: 'Booked',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Cancelled: 'Cancelled',
};

/** Reuses the existing allocation side panel (incl. event timeline) for drilldown. */
function ActivityAllocationPanel({ allocationId }: { allocationId: string }) {
  return <AllocationDetailsPanel allocationId={allocationId} />;
}

export default function ActivityPage() {
  const { isOpen, id, type, openPanel, closePanel } = useSitePanelState();
  const ui = useActivityUIStore();
  const { data: sites = [] } = useActivitySites();
  const [exportOpen, setExportOpen] = useState(false);
  // Track only what the user has explicitly collapsed — so every week is
  // expanded by default, including weeks arriving with the next page.
  const [collapsedWeeks, setCollapsedWeeks] = useState<ReadonlySet<string>>(
    new Set()
  );

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useActivityList({
    status: ui.statusFilter,
    type: ui.typeFilter,
    siteIds: ui.siteIdsFilter,
    from: ui.fromDate,
    to: ui.toDate,
  });

  const items = useMemo(() => data?.pages.flatMap(p => p.items) ?? [], [data]);
  const totalCount = data?.pages[0]?.totalCount ?? 0;
  const weeks = useMemo(() => toActivityRows(items), [items]);

  const toggleWeek = (key: string) =>
    setCollapsedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const handleRowClick = (row: ActivityLedgerRowModel) =>
    openPanel(
      'allocation',
      row.id,
      row.siteId.toString(),
      row.plotId.toString()
    );

  const isFilterActive =
    ui.statusFilter !== '' ||
    ui.typeFilter !== '' ||
    ui.siteIdsFilter.length > 0;

  const filterChips = (
    <>
      {ui.statusFilter && (
        <Chip
          size="small"
          label={STATUS_LABELS[ui.statusFilter] ?? ui.statusFilter}
          onDelete={() => ui.setStatusFilter('')}
          sx={{ height: 26, borderRadius: 1 }}
        />
      )}
      {ui.typeFilter && (
        <Chip
          size="small"
          label={ui.typeFilter === 'Labour' ? 'Labour' : 'Variations'}
          onDelete={() => ui.setTypeFilter('')}
          sx={{ height: 26, borderRadius: 1 }}
        />
      )}
      {ui.siteIdsFilter.map(siteId => {
        const site = sites.find(s => s.id === siteId);
        return (
          <Chip
            key={siteId}
            size="small"
            label={site?.name ?? `Site ${siteId}`}
            onDelete={() => ui.toggleSiteId(siteId)}
            sx={{ height: 26, borderRadius: 1 }}
          />
        );
      })}
    </>
  );

  const panelContent = (
    <>
      {isOpen && type === 'allocation' && id && (
        <>
          <ResizableHandle />
          <SidePanel open={isOpen} onClose={closePanel} title="Booking">
            <ActivityAllocationPanel allocationId={id} />
          </SidePanel>
        </>
      )}
    </>
  );

  return (
    <PageContentArea isPanelOpen={isOpen} panel={panelContent}>
      <PageHeader
        title="Activity"
        actions={
          <Button
            size="small"
            variant="text"
            startIcon={
              <FileDownloadIcon sx={{ fontSize: '0.875rem !important' }} />
            }
            onClick={() => setExportOpen(true)}
            sx={{ color: 'text.secondary' }}
            data-testid="activity-export"
          >
            <Box
              component="span"
              sx={{ display: { xs: 'none', sm: 'inline' } }}
            >
              Export
            </Box>
          </Button>
        }
      />
      <PageToolbar
        tabs={
          <DateRangeControl
            from={ui.fromDate}
            to={ui.toDate}
            onChange={ui.setDateRange}
          />
        }
        isFilterActive={isFilterActive}
        filterChips={filterChips}
        filterContent={
          <ActivityFilterContent
            status={ui.statusFilter}
            onStatusChange={ui.setStatusFilter}
            siteIds={ui.siteIdsFilter}
            onSitesChange={ui.setSiteIds}
            type={ui.typeFilter}
            onTypeChange={ui.setTypeFilter}
          />
        }
      />

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          ...ledgerContainerSx(ACTIVITY_COLLAPSE.container),
        }}
      >
        {isLoading && !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ justifyContent: 'center' }}>
              Couldn&apos;t load activity.{' '}
              <Button size="small" onClick={() => fetchNextPage()}>
                Try again
              </Button>
            </Alert>
          </Box>
        ) : weeks.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No activity in this period
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Nothing was booked for the current filters.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const to = new Date();
                  const from = new Date(Date.now() - 29 * MS_DAY);
                  ui.setDateRange(from.toISOString(), to.toISOString());
                }}
              >
                Widen to last 30 days
              </Button>
              {isFilterActive && (
                <Button variant="text" size="small" onClick={ui.clearFilters}>
                  Clear filters
                </Button>
              )}
            </Stack>
          </Box>
        ) : (
          <ActivityLedger
            weeks={weeks}
            collapsedWeeks={collapsedWeeks}
            onToggleWeek={toggleWeek}
            onOpenRow={handleRowClick}
            totalCount={totalCount}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
          />
        )}
      </Box>

      <ExportActivityModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        from={ui.fromDate}
        to={ui.toDate}
        siteIds={ui.siteIdsFilter}
        type={ui.typeFilter}
      />
    </PageContentArea>
  );
}
